import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import chatApi from '../services/chatApi';
import openaiService from '../services/openaiService';

export const useChatStore = defineStore('chat', () => {
  const messages = ref([]);
  const sessionId = ref(null);
  const conversationHistory = ref([]); // Historial completo para análisis GPT
  const isEscalated = ref(false);
  const isChatClosed = ref(false); // Nueva ref para controlar si el chat está cerrado

  const addUserMessage = async (text) => {
    const messageId = Date.now();
    messages.value.push({
      id: messageId,
      from: 'user',
      text,
      status: 'sending', // sending -> delivered -> read
      timestamp: new Date()
    });
    return messageId;
  };
  
  const updateMessageStatus = (messageId, status) => {
    const message = messages.value.find(m => m.id === messageId);
    if (message) {
      message.status = status;
    }
  };

  const addEliasMessage = async (text, payload = null) => {
    messages.value.push({
      id: Date.now() + 1,
      from: 'elias',
      text,
      payload, // Almacenar el payload completo
    });
  };

  const addSystemMessage = async (text) => {
    messages.value.push({
      id: Date.now() + 2,
      from: 'system',
      text,
    });
  };

  const sendMessageToElias = async (messageText) => {
    try {
      // 1. Agregar mensaje del usuario al historial
      conversationHistory.value.push({ from: 'user', text: messageText, timestamp: new Date().toISOString() });
      
      // 2. Obtener información del usuario autenticado
      const userData = JSON.parse(localStorage.getItem('ecomac_user') || '{}');
      console.log('📧 Datos del usuario obtenidos del localStorage (ecomac_user):', userData);
      
      // 3. Preparar payload con historial completo y datos de usuario
      const payload = {
        // Mensaje actual del usuario
        currentMessage: messageText,
        
        // Historial completo de la conversación
        conversationHistory: conversationHistory.value,
        
        // Información de la sesión
        sessionId: sessionId.value,
        timestamp: new Date().toISOString(),
        
        // Datos del usuario autenticado
        user: {
          name: userData.name || userData.fullName || 'Usuario no identificado',
          email: userData.email || 'email@no-disponible.com',
          id: userData.id || userData.userId || 'ID-no-disponible'
        },
        
        // Metadatos
        source: 'portal-soporte',
        userAgent: navigator.userAgent
      };
      
      // Log del payload completo para debug
      console.log('📦 Payload enviado al webhook N8N:', JSON.stringify(payload, null, 2));
      
      // 4. Si no necesita escalamiento, usar webhook de conversación normal
      const response = await fetch('https://inmobiliaria-ecomac.app.n8n.cloud/webhook/portal-gestion-tickets/agente-conversacional-elias-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // 5. Procesar respuesta
      let eliasResponseText;
      let eliasResponsePayload = null;
      const responseText = await response.text();
      
      try {
        const jsonResponse = JSON.parse(responseText);
        
        // Detectar formato actual del webhook (v3.0 - Estructura output.copy + output.data)
        if (Array.isArray(jsonResponse) && jsonResponse[0]?.output) {
          const outputData = jsonResponse[0].output;
          
          if (outputData.copy) {
            // Formato actual confirmado: { output: { copy: "texto", data: {...} } }
            eliasResponseText = outputData.copy;
            eliasResponsePayload = outputData.data || null; // Guardar data completa
            console.log('✅ Formato actual detectado (v3.0):', {
              intent: outputData.data?.intent,
              state: outputData.data?.state,
              attempts: outputData.data?.attempts,
              next: outputData.data?.next
            });
          } else if (typeof outputData === 'string') {
            // Fallback si output es string directo
            eliasResponseText = outputData;
            console.log('✅ Formato output string detectado');
          } else {
            console.warn('⚠️ Estructura output no reconocida:', outputData);
            eliasResponseText = 'He recibido tu mensaje, pero necesito ajustar el formato de respuesta. 🔧';
          }
        }
        // Compatibilidad con formato v2.1 (message.content)
        else if (Array.isArray(jsonResponse) && jsonResponse[0]?.message?.content) {
          const content = jsonResponse[0].message.content;
          eliasResponseText = content.copy;
          eliasResponsePayload = content.payload;
          console.log('✅ Formato v2.1 detectado (message.content)');
        }
        // Fallbacks para formatos anteriores
        else {
          console.log('⚠️ Usando fallback para formato de respuesta no reconocido.');
          if (jsonResponse.solicitud && jsonResponse.estado) {
            eliasResponseText = '(Respuesta estructurada anterior procesada)';
          } else if (jsonResponse.output) {
            eliasResponseText = jsonResponse.output;
          } else if (jsonResponse.message) {
            eliasResponseText = jsonResponse.message;
          } else if (jsonResponse.response) {
            eliasResponseText = jsonResponse.response;
          } else {
            console.warn('⚠️ Respuesta JSON completamente no reconocida:', jsonResponse);
            eliasResponseText = 'He recibido tu mensaje, pero la respuesta tiene un formato que necesito ajustar. ¿Podrías intentar reformular tu pregunta? 🔧';
          }
        }
      } catch (parseError) {
        console.warn('⚠️ Error al parsear JSON, usando texto plano:', parseError);
        eliasResponseText = responseText || 'Lo siento, hubo un problema al procesar tu mensaje. ¿Puedes intentar de nuevo? 🔄';
      }
      
      console.log('🤖 Respuesta de Elias AI:', { text: eliasResponseText, payload: eliasResponsePayload });
      
      // 6. Agregar respuesta de Elias al historial y al estado de mensajes
      conversationHistory.value.push({ 
        from: 'elias', 
        text: eliasResponseText, 
        timestamp: new Date().toISOString() 
      });
      await addEliasMessage(eliasResponseText, eliasResponsePayload); // Llamada única para añadir el mensaje
      
      // 7. Actualizar estado del chat basado en el payload
      updateChatState(eliasResponsePayload);
      
      return { message: eliasResponseText };
      
    } catch (error) {
      console.error('❌ Error en sendMessageToElias:', error);
      await addSystemMessage('❌ Error al comunicarse con Elias AI. Por favor, intenta de nuevo.');
      throw error;
    }
  };
  
  // Función de análisis local para detectar escalamiento
  const analyzeEscalationLocally = (history, currentMessage) => {
    const userMessages = history.filter(msg => msg.from === 'user');
    const lastUserMessages = userMessages.slice(-3); // Últimos 3 mensajes del usuario
    
    // Palabras clave que indican persistencia del problema
    const persistenceKeywords = [
      'aun', 'aún', 'todavía', 'sigue', 'persiste', 'continúa',
      'he probado', 'he intentado', 'ya probé', 'ya intenté',
      'no funciona', 'no sirve', 'sigue sin funcionar',
      'mismo error', 'mismo problema', 'igual error',
      'que puedo hacer', 'qué puedo hacer', 'que más puedo hacer',
      'necesito ayuda', 'no se que hacer', 'no sé qué hacer'
    ];
    
    // Palabras clave de frustración/urgencia
    const frustrationKeywords = [
      'frustrado', 'molesto', 'urgente', 'importante',
      'necesito resolver', 'es crítico', 'no puedo trabajar'
    ];
    
    // Verificar si hay indicadores de persistencia
    const currentText = currentMessage.toLowerCase();
    const hasPersistenceIndicators = persistenceKeywords.some(keyword => 
      currentText.includes(keyword.toLowerCase())
    );
    
    const hasFrustrationIndicators = frustrationKeywords.some(keyword => 
      currentText.includes(keyword.toLowerCase())
    );
    
    // Verificar si el usuario ha mencionado haber probado soluciones
    const hasTriedSolutions = currentText.includes('he probado') || 
                             currentText.includes('he intentado') ||
                             currentText.includes('ya probé') ||
                             currentText.includes('ya intenté');
    
    // Determinar si debe escalar
    let shouldEscalate = false;
    let confidence = 30;
    let reasoning = 'Análisis local básico';
    
    // Escalamiento por persistencia del problema después de múltiples intercambios
    if (userMessages.length >= 3 && hasPersistenceIndicators) {
      shouldEscalate = true;
      confidence = 85;
      reasoning = 'Usuario indica que el problema persiste después de múltiples intercambios';
    }
    // Escalamiento por frustración y haber probado soluciones
    else if (hasTriedSolutions && hasPersistenceIndicators) {
      shouldEscalate = true;
      confidence = 80;
      reasoning = 'Usuario indica haber probado soluciones y el problema persiste';
    }
    // Escalamiento por frustración alta
    else if (hasFrustrationIndicators && userMessages.length >= 2) {
      shouldEscalate = true;
      confidence = 70;
      reasoning = 'Usuario muestra frustración y múltiples mensajes';
    }
    // Escalamiento por muchos mensajes sin resolución
    else if (userMessages.length >= 4) {
      shouldEscalate = true;
      confidence = 75;
      reasoning = 'Conversación larga sin resolución aparente';
    }
    
    return {
      shouldEscalate,
      confidence,
      reasoning,
      hasTechnicalProblem: true,
      problemSolved: false
    };
  };

  const clearChat = async () => {
    messages.value = [];
    conversationHistory.value = [];
    isEscalated.value = false;
  };

  const startNewSession = async () => {
    sessionId.value = await chatApi.startSession();
    clearChat();
    isChatClosed.value = false; // Resetear estado de chat cerrado al iniciar nueva sesión
  };

  const updateChatState = (payload) => {
    // Verificar si el payload contiene action "Cerrar chat"
    if (payload && payload.action === 'Cerrar chat') {
      isChatClosed.value = true;
      console.log('🔒 Chat cerrado por acción del webhook');
    }
  };

  return {
    messages,
    sessionId,
    conversationHistory: computed(() => conversationHistory.value),
    isEscalated,
    isChatClosed: computed(() => isChatClosed.value),
    addUserMessage,
    addEliasMessage,
    addSystemMessage,
    sendMessageToElias,
    updateMessageStatus,
    updateChatState,
    clearChat,
    startNewSession,
  };
});
